import { z } from "zod/v4";

import { CustomEventDefinitionSchema } from "../../dtos/analytics-events";
import { ExperienceCategoryList } from "../../dtos/experience-categories";
import {
  ExperienceDtoSchema,
  ExperienceTestTypeDtoSchema,
} from "../../dtos/experiences";
import { castToBool, castToNumber, castToString } from "../../utils";

import {
  AnalyticsChannelList,
  AnalyticsResourceTypeV2List,
  AnalyzeContext,
  CastedTimesTinybird,
  ConversionStageReachedList,
  CountryCodesFilterList,
  CustomerTypeList,
  CustomEventsFilterTypeList,
  DatasetIdList,
  DeviceTypeList,
  LandingPageFilterItemSchema,
  LandingPageFiltersOperatorList,
  LandingPagePayloadSchema,
  LandingPageTypeList,
  RequestSourceList,
  UrlParamPayloadSchema,
  VisitorTypeSchema,
} from "./base";
import { ExperienceAnalyticsFiltersSchema } from "./filters";

export const ExperienceSpanSchema = z.object({
  start_ts: z.number(),
  end_ts: z.number(),
});

export const ExperienceKeyMetricPayloadSchema = z.object({
  metricKey: z.string(),
  isPrimary: z.boolean(),
});
export type ExperienceKeyMetricPayloadType = z.infer<
  typeof ExperienceKeyMetricPayloadSchema
>;

export const BaseExperienceTinybirdAnalyticsPayloadSchema = z.object({
  datasetId: z.enum(DatasetIdList),
  startTs: castToString.or(z.number()).nullable(),
  endTs: castToString.or(z.number()).nullable(),
  customTimeWindow: z.boolean().optional(),
  shopifyDomain: z.string(),
  latestRunTs: z.string(),
  timezone: z.string(),
  urlParam: UrlParamPayloadSchema,
  landingPage: LandingPagePayloadSchema.nullish(),
  landingPageFilters: LandingPageFilterItemSchema.array().optional(),
  landingPageFiltersOperator: z.enum(LandingPageFiltersOperatorList).optional(),
  landingPageTypes: z.enum(LandingPageTypeList).array().optional(),
  deviceType: z.enum(DeviceTypeList).nullable(),
  customerType: z.enum(CustomerTypeList).nullable(),
  visitorType: z.enum(VisitorTypeSchema).nullable(),
  // AnalyticsChannelList, not ChannelTypeSchema — this payload is forwarded to
  // analytics-v2, whose own channel enum is AnalyticsChannelList. See ./base.
  sourceSitesOrChannels: z.enum(AnalyticsChannelList).array(),
  shippingExpensePerOrder: castToNumber.or(z.string()).nullable(),
  transactionFeePct: castToNumber.or(z.string()).nullable(),
  countryCodes: z.string().array(),
  countryCodesFilterType: z
    .enum(CountryCodesFilterList)
    .default("include")
    .optional(),
  metrics: z.string().array().default([]),
  userBehavior: z.object({
    viewedCheckout: z.boolean().nullable(),
    addedToCart: z.boolean().nullable(),
    viewedProductPage: z.boolean().nullable().optional(),
    viewedCollectionPage: z.boolean().nullable().optional(),
  }),
  maxZScore: castToNumber.nullable(),
  experienceId: z.uuid(),
  triggeredCustomEvents: z.string().array().default([]),
  audience: z.string().nullish(),
  interval: z.string().nullish(),
  topN: z.any().optional(),
  topNProducts: z.any().optional(),
  histogramMetric: z.string().nullish(),
  kind: z.string().nullish(),
  experimentProductsOnly: castToBool.nullable(),
  reachedCheckoutStage: z.enum(ConversionStageReachedList).nullable(),
  return_variants: z.boolean().optional(),
  showExperimentProductsOnly: castToBool.nullable().optional(), // only the experiment products
  customEventsFilterType: z
    .enum(CustomEventsFilterTypeList)
    .default("all")
    .optional(),
  minNetRevenue: castToNumber.or(z.string()).nullish(),
  maxNetRevenue: castToNumber.or(z.string()).nullish(),
  minShippingRevenue: castToNumber.or(z.string()).nullish(),
  maxShippingRevenue: castToNumber.or(z.string()).nullish(),
  shippingMethods: z.string().array().default([]),
  onlyProductIds: z.string().array().nullish(),
  onlyProductHandles: z.string().array().nullish(),
  checkoutBlockIds: z.string().array().optional(),
  onsiteUpsellIds: z.string().array().optional(),
  excludeFreeOrders: z.boolean().optional(),
  includeTestOrders: z.boolean().optional(),
  includePostPurchaseOfferBreakdown: z.boolean().optional(),
  includeCheckoutUpsellBreakdown: z.boolean().optional(),
  includeOnsiteUpsellBreakdown: z.boolean().optional(),
  testResult: z.boolean().optional(),
  keyMetrics: ExperienceKeyMetricPayloadSchema.array().optional(),
});

// Node Backend to Analytics Request
export const AnalyzeExperienceBackendToTinybirdRequestSchema =
  BaseExperienceTinybirdAnalyticsPayloadSchema.extend({
    controlVariationId: z.uuid(),
    experienceStartTs: z.number(),
    experienceSpans: ExperienceSpanSchema.array().default([]),
    customEventDefinitions: CustomEventDefinitionSchema.array().optional(),
    excludeIpAddresses: z.string().array(),
    useOriginal: z.boolean(),
    excludeOrderTags: z.string().array(),
    experienceType: z.enum(ExperienceCategoryList),
    testTypes: ExperienceTestTypeDtoSchema,
    isHeadless: z.boolean(),
    checkoutUpsellProductsOnly: castToBool.nullable(),
    onsiteUpsellProductsOnly: castToBool.nullable(),
    requestSource: z.enum(RequestSourceList).optional().default("internal"),
  });

export type AnalyzeExperienceBackendToTinybirdRequestType = z.infer<
  typeof AnalyzeExperienceBackendToTinybirdRequestSchema
>;

// Frontend to Node Backend Request
export const AnalyzeExperienceFrontendRequestBodySchema =
  BaseExperienceTinybirdAnalyticsPayloadSchema.omit({
    shopifyDomain: true,
    latestRunTs: true,
    timezone: true,
  }).extend({
    baselineVariationId: z.uuid().optional(),
    resourceType: z.enum(AnalyticsResourceTypeV2List),
    /** Pass 0 to enable with server default, a positive number for a custom threshold, or omit to disable. */
    timeseriesDownSampleMaxRows: z.number().optional(),
  });

export const FileExportTypeSchema = [
  "orders",
  "order_line_items",
  "order_reconciliation",
] as const;
export type FileExportType = (typeof FileExportTypeSchema)[number];

// The full internal export-type domain the analytics-v2 create-export endpoint
// accepts. `experience_session_order` is staff-only and is deliberately kept out
// of the public/customer-facing FileExportTypeSchema above.
export const ExperienceExportTypeSchema = [
  "orders",
  "order_line_items",
  "order_reconciliation",
  "experience_session_order",
] as const;
export type ExperienceExportType = (typeof ExperienceExportTypeSchema)[number];

export const FileExportExperienceFrontendRequestBodySchema =
  AnalyzeExperienceFrontendRequestBodySchema.extend({
    exportType: z.enum(FileExportTypeSchema),
  });

// Manager-level export request. Widened to the full export-type domain so the
// staff export path can flow `experience_session_order` through the shared
// fileExportTinybird call. The narrower frontend request body is a subtype.
export const ExportExperienceTinybirdRequestBodySchema =
  AnalyzeExperienceFrontendRequestBodySchema.extend({
    exportType: z.enum(ExperienceExportTypeSchema),
  });

export type ExportExperienceTinybirdRequestBodyType = z.infer<
  typeof ExportExperienceTinybirdRequestBodySchema
>;

export const FileExportExperienceResponseBodySchema = z.object({
  message: z.string(),
  url: z.string().optional(),
});

export type FileExportExperienceResponseBodyType = z.infer<
  typeof FileExportExperienceResponseBodySchema
>;

export type FileExportExperienceFrontendRequestBodyType = z.infer<
  typeof FileExportExperienceFrontendRequestBodySchema
>;

export type FileExportExperienceFrontendResponseBodyType =
  FileExportExperienceResponseBodyType;

export type FileExportExperienceAnalyticsResponseBodyType = {
  job_url?: string;
  s3_bucket?: string;
  s3_path?: string;
  message: string;
};

export type AnalyzeExperienceFrontendTinybirdRequestBodyType = z.infer<
  typeof AnalyzeExperienceFrontendRequestBodySchema
> &
  CastedTimesTinybird;

export const AnalyzeExperienceTinybirdContextSchema = AnalyzeContext.extend({
  experience: ExperienceDtoSchema,
  baselineVariationId: z.uuid(),
  numVariations: z.number(),
  formattedCustomEventDefinitions:
    CustomEventDefinitionSchema.array().optional(),
});

export type AnalyzeExperienceTinybirdContextType = z.infer<
  typeof AnalyzeExperienceTinybirdContextSchema
>;
